"use client";

import { useEffect, useState, useEffectEvent } from "react";
import {
  getRankHistory,
  pickBucketSeconds,
  type RankHistoryPoint,
} from "../api";
import type { RankListType } from "../types";
import { isAbortError } from "@/lib/utils/error";
import { useChartCache } from "@/hooks/useChartCache";

const getTs = (p: RankHistoryPoint) => p.timestamp;

type UseRankHistoryOptions = {
  type: RankListType;
  appId: number;
  from: number;
  to: number;
  cc?: string;
  enabled?: boolean;
};

export function useRankHistory({
  type,
  appId,
  from,
  to,
  cc,
  enabled = true,
}: UseRankHistoryOptions) {
  const [history, setHistory] = useState<RankHistoryPoint[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { trySlice, store } = useChartCache<RankHistoryPoint>(getTs);

  const handleStart = useEffectEvent(() => {
    setIsLoading(true);
    setError(null);
  });

  const handleSuccess = useEffectEvent((result: RankHistoryPoint[]) => {
    setHistory(result);
    setIsLoading(false);
  });

  const handleError = useEffectEvent((message: string) => {
    setHistory([]);
    setError(message);
    setIsLoading(false);
  });

  useEffect(() => {
    if (!enabled) {
      handleSuccess([]);
      return;
    }

    // Bucket в ключі — щоб не віддавати кешовані рідкі дані (наприклад,
    // 6h-бакети з 3м запиту) у відповідь на дрібніший діапазон, який має
    // вертати raw 10-хв точки.
    const bucket = pickBucketSeconds(from > 0 ? Math.max(0, to - from) : Number.MAX_SAFE_INTEGER);
    const cacheKey = `${type}:${appId}:${cc ?? ""}:${bucket}`;
    const cached = trySlice(cacheKey, from, to);
    if (cached) {
      handleSuccess(cached);
      return;
    }

    let cancelled = false;
    const controller = new AbortController();
    handleStart();

    getRankHistory({
      type,
      appId,
      from,
      to,
      cc,
      init: { signal: controller.signal },
    })
      .then((result) => {
        if (!cancelled) {
          store(cacheKey, from, to, result);
          handleSuccess(result);
        }
      })
      .catch((err: Error) => {
        // Ignore aborts (usually caused by fast switching)
        if (isAbortError(err)) return;
        if (!cancelled) {
          handleError(err.message ?? "Unknown error");
        }
      });

    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [type, appId, from, to, cc, enabled]);

  return { history, isLoading, error };
}
